/**
 * Daily scorecard: sort + report-status filter helpers (issue #510).
 *
 * Plain functions in their own .ts file (not HqScorecard.tsx) so react-refresh's
 * only-export-components rule doesn't flag mixing them with page component
 * exports. Mirrors the client-side sort pattern in ContentList.tsx
 * (parseSortParam / sortRows), URL-encoded as `?sort=col:asc|desc`.
 */

import type { ScorecardRow } from '@/lib/useStores';

export const SCORECARD_SORT_COLUMNS = [
  'store',
  'open',
  'midday',
  'close',
  'photos',
  'traffic',
  'transactions',
  'questions',
] as const;
export type ScorecardSortColumn = (typeof SCORECARD_SORT_COLUMNS)[number];
export type ScorecardSortDir = 'asc' | 'desc';

export type ScorecardReportFilter = 'missing' | 'complete';

// Filter chip row order: All, then most-actionable first.
export const SCORECARD_REPORT_FILTERS: { value: ScorecardReportFilter | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'missing', label: 'Missing a report' },
  { value: 'complete', label: 'All reports in' },
];

// How many of the three phases a store submitted.
export function submittedCount(r: ScorecardRow): number {
  return (r.open_submitted ? 1 : 0) + (r.midday_submitted ? 1 : 0) + (r.close_submitted ? 1 : 0);
}

export function filterScorecardRows(
  rows: ScorecardRow[],
  filter: ScorecardReportFilter | null,
): ScorecardRow[] {
  if (filter === 'missing') return rows.filter((r) => submittedCount(r) < 3);
  if (filter === 'complete') return rows.filter((r) => submittedCount(r) === 3);
  return rows;
}

function sortValue(row: ScorecardRow, col: ScorecardSortColumn): string | number {
  switch (col) {
    case 'store':
      return row.store_name.toLowerCase();
    case 'open':
      return row.open_submitted ? 1 : 0;
    case 'midday':
      return row.midday_submitted ? 1 : 0;
    case 'close':
      return row.close_submitted ? 1 : 0;
    case 'photos':
      return row.photo_count;
    case 'traffic':
      return row.traffic ?? -1;
    case 'transactions':
      return row.transactions ?? -1;
    case 'questions':
      return row.open_questions;
  }
}

// Client-side sort: the scorecard endpoint returns every active store in one
// response. Ties break alphabetically so equal-value rows (e.g. two stores
// that haven't submitted anything) don't jitter as the query refetches.
export function sortScorecardRows(
  rows: ScorecardRow[],
  col: ScorecardSortColumn,
  dir: ScorecardSortDir,
): ScorecardRow[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, col);
    const bv = sortValue(b, col);
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return a.store_name.localeCompare(b.store_name);
  });
}

export function parseScorecardSort(raw: string | null): {
  column: ScorecardSortColumn;
  dir: ScorecardSortDir;
} {
  if (raw) {
    const [col, dir] = raw.split(':');
    if (col && (SCORECARD_SORT_COLUMNS as readonly string[]).includes(col)) {
      return { column: col as ScorecardSortColumn, dir: dir === 'desc' ? 'desc' : 'asc' };
    }
  }
  // Default: alphabetical by store name. Replaces the old implicit
  // missing-first sort, which had no visible indicator (issue #510).
  return { column: 'store', dir: 'asc' };
}
